import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import AuthUtility from "../../util/AuthUtility";
import { Col, Button, Row, Container, Card, Form, InputGroup, Table, Pagination, Badge } from 'react-bootstrap';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import toast, { Toaster } from 'react-hot-toast';
import { onProductSearch } from "../../controller/ProductController";
import { Page } from "../../dto/page/Page";
import { BankIncomingTxnEntity } from "../../dto/bank/BankIncomingTxnEntity";
import { BankIncomingTxnListEntity } from "../../dto/bank/BankIncomingTxnListEntity";
import { BankIncomingTxnSearch } from "../../dto/bank/BankIncomingTxnSearch";
import { onBankTxnSearch } from "../../controller/BankController";
import { getBankTxnStatusBadge } from "../../app/AppConst";

const BankIncomingTxnList = () => {
    const navigate = useNavigate();
    useEffect(() => {
        if (!AuthUtility.isUserLoggedIn()) {
            navigate("/login");
            window.location.reload();
        }
    }, []);

    const [bankTxn, setBankTxn] = useState<BankIncomingTxnListEntity>({
        data: []
    });

    const [search, setSearch] = useState<BankIncomingTxnSearch>({
        offset: -1,
        limit: 10
    });

    // ---- Search submit
    const searchSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        load(0); // reset to first page on new search
    };

    // ---- Pagination click
    const changePage = (page: number) => {
        if (!bankTxn.page) return;
        if (page < 0 || page >= bankTxn.page.totalPages) return;
        load(page);
    };

    const load = async (page: number = 0) => {
        const params: BankIncomingTxnSearch = {
            ...search,
            offset: page
        };

        const response = await onBankTxnSearch(params);

        if (response.status !== 0) {
            toast.error("Банкны гүйлгээ уншихад алдаа гарлаа");
            return;
        }

        const pageable = response.data;

        const pageInfo: Page = {
            limit: search.limit,
            totalElements: pageable.totalElements,
            totalPages: pageable.totalPages,
            offset: page
        };

        const beans: Array<BankIncomingTxnEntity> = pageable.beans;

        setBankTxn({
            page: pageInfo,
            data: beans
        });

        setSearch(prev => ({ ...prev, offset: page }));
    };

    return (
        <Container fluid>
            <div><Toaster position="top-right" reverseOrder={false}/></div>
            <h3><a onClick={() => navigate(-1)}><big>&#8592;</big></a>Bank Transactions</h3>
            <Row className="d-flex justify-content-center align-items-center mb-3">
                <Col md={12} lg={12} xs={12}>
                    <Card>
                        <Card.Header>
                            <Form onSubmit={searchSubmit}>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="name">
                                            <Form.Label className="text-center">Sender Account No</Form.Label>
                                            <Form.Control size="sm"
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    if (value !== "") {
                                                        setSearch({
                                                            ...search,
                                                            senderAccountNo: event.target.value
                                                        })
                                                    } else {
                                                        const { senderAccountNo, ...rest } = search;
                                                        setSearch(rest);
                                                    }
                                                }} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3" controlId="offSet">
                                            <Form.Label>Багцлах</Form.Label>
                                            <InputGroup className="mb-3">
                                                <Form.Select size="sm"
                                                    onChange={(event) => setSearch({
                                                        ...search,
                                                        limit: parseInt(event.target.value)
                                                    })}>
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="0">Бүгд</option>
                                                </Form.Select>
                                                <Button variant="primary" size="sm" type="submit">
                                                    <SearchIcon fontSize="small"/>
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Header>
                        <Card.Body>
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>Created At</th>
                                        <th>Transaction Date</th>
                                        <th>Status</th>
                                        <th>Company Account</th>
                                        <th>Sender Account</th>
                                        <th>Record No</th>
                                        <th>Journal No</th>
                                        <th>Currency</th>
                                        <th>Amount</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bankTxn.data?.length === 0 && (
                                        <tr>
                                            <td className="text-muted">
                                                Илэрц олдсонгүй
                                            </td>
                                        </tr>
                                    )}

                                    {
                                        bankTxn.data?.map((data, idx) => {
                                            return (
                                                <tr key={idx}>
                                                    <td>{data.createdAt
                                                    ? new Date(data.createdAt).toLocaleString()
                                                    : "-"}</td>
                                                    <td>{data.txnDate
                                                    ? new Date(data.txnDate).toLocaleString()
                                                    : "-"}</td>
                                                    <td>
                                                        <Badge bg={getBankTxnStatusBadge(data.status)}>{data.status}</Badge>
                                                    </td>
                                                    <td>{data.companyAccountNo}</td>
                                                    <td>{data.senderAccountNo}</td>
                                                    <td>{data.recordNo}</td>
                                                    <td>{data.journalNo}</td>
                                                    <td>{data.currency}</td>
                                                    <td>{data.amount}</td>
                                                    <td>{data.description}</td>
                                                </tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>  
            </Row>

            {bankTxn.page && bankTxn.page.totalPages > 1 && (() => {
                const page = bankTxn.page;   // now TypeScript knows it's NOT undefined

                return (
                    <Row>
                        <Col className="d-flex justify-content-start">
                            <Pagination>
                                <Pagination.First
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(0)}
                                />

                                <Pagination.Prev
                                    disabled={page.offset === 0}
                                    onClick={() => changePage(page.offset - 1)}
                                />

                                {Array.from({ length: page.totalPages })
                                    .slice(
                                        Math.max(page.offset - 2, 0),
                                        Math.min(page.offset + 3, page.totalPages)
                                    )
                                    .map((_, idx) => {
                                        const pageNumber =
                                            Math.max(page.offset - 2, 0) + idx;

                                        return (
                                            <Pagination.Item
                                                key={pageNumber}
                                                active={pageNumber === page.offset}
                                                onClick={() => changePage(pageNumber)}
                                            >
                                                {pageNumber + 1}
                                            </Pagination.Item>
                                        );
                                    })}

                                <Pagination.Next
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.offset + 1)}
                                />

                                <Pagination.Last
                                    disabled={page.offset + 1 >= page.totalPages}
                                    onClick={() => changePage(page.totalPages - 1)}
                                />
                            </Pagination>
                        </Col>
                    </Row>
                );
            })()}
        </Container>
    )
}

export default BankIncomingTxnList;